import React, { useState, useEffect, useRef } from "react";
import { CircularProgress } from "@mui/material";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ErrorIcon from "@mui/icons-material/Error";
import MSwitch from "../../common/switch";
import {
  disableAutomation,
  enableAutomation,
  runningAutomation,
} from "../../service/automation-service";
import { useToast } from "../../common/toast";
import { useWebSocket } from "../../ws/websocket";
import { Typography, IconButton } from "@mhome/ui";

const AutomationTitle1Widget = ({
  widget,
  onStateUpdate,
  onTitleClick,
  onTitleHold,
}) => {
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();

  const [title, setTitle] = useState(widget.title);
  const [subtitle, setSubtitle] = useState(widget.subtitle);

  const [automationType, setAutomationType] = useState("");
  const [automationId, setAutomationId] = useState("");
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [valid, setValid] = useState(true);

  const touchStateRef = useRef({
    startPos: { x: 0, y: 0 },
    startTime: 0,
    hasScrolled: false,
    longPressTriggered: false,
    pressTimer: null,
  });
  const mousePressTimerRef = useRef(null);
  const mouseLongPressTriggeredRef = useRef(false);

  useEffect(() => {
    setTitle(widget.title);
    setSubtitle(widget.subtitle);
    setAutomationType(widget.automationType);
    setAutomationId(widget.automationId);
    setRunning(widget.running);
    setEnabled(widget.enabled);
    setValid(widget.valid);
  }, [widget]);

  const handleEnableDisable = async (event, enableDisable) => {
    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        setEnabled(response.enabled);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: response.enabled,
          });
      } else {
        errorToast("Error!");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        setEnabled(response.enabled);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: response.enabled,
          });
      } else {
        errorToast("Error!");
      }
    }
  };

  const handleStartTaskFlow = async () => {
    const success = await runningAutomation(sendAndWaitWs, automationId);
    if (success) {
      successToast("Started");
    } else {
      errorToast("Error!");
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (event) => {
    if (!event.touches || !event.touches[0] || !onTitleHold) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;
    const targetElement = event.currentTarget;

    // Reset touch state
    touchState.startPos = { x: touch.clientX, y: touch.clientY };
    touchState.startTime = Date.now();
    touchState.hasScrolled = false;
    touchState.longPressTriggered = false;

    // Clear any existing timer
    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
    }

    // Start long press timer
    touchState.pressTimer = setTimeout(() => {
      if (!touchState.hasScrolled && !touchState.longPressTriggered) {
        touchState.longPressTriggered = true;
        onTitleHold(targetElement);
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 800);
  };

  const handleTouchMove = (event) => {
    if (!event.touches || !event.touches[0]) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;

    if (touchState.hasScrolled) return;

    const deltaX = Math.abs(touch.clientX - touchState.startPos.x);
    const deltaY = Math.abs(touch.clientY - touchState.startPos.y);

    if (deltaX > 10 || deltaY > 10) {
      touchState.hasScrolled = true;

      if (touchState.pressTimer) {
        clearTimeout(touchState.pressTimer);
        touchState.pressTimer = null;
      }
    }
  };

  const handleTouchEnd = (event) => {
    const touchState = touchStateRef.current;

    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
      touchState.pressTimer = null;
    }

    if (touchState.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
    }

    setTimeout(() => {
      touchState.hasScrolled = false;
      touchState.longPressTriggered = false;
    }, 100);
  };

  // Mouse handlers for desktop
  const handleMouseDown = (event) => {
    if (!onTitleHold) return;
    const targetElement = event.currentTarget;
    mouseLongPressTriggeredRef.current = false;
    mousePressTimerRef.current = setTimeout(() => {
      mouseLongPressTriggeredRef.current = true;
      onTitleHold(targetElement);
    }, 800);
  };

  const handleMouseUp = () => {
    if (mousePressTimerRef.current) {
      clearTimeout(mousePressTimerRef.current);
      mousePressTimerRef.current = null;
    }
    // Reset the flag after a short delay to allow click handler to check it
    if (mouseLongPressTriggeredRef.current) {
      setTimeout(() => {
        mouseLongPressTriggeredRef.current = false;
      }, 100);
    }
  };

  const handleClick = (event) => {
    // Prevent click if we just finished scrolling
    if (touchStateRef.current.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Prevent click if long press was triggered (touch)
    if (touchStateRef.current.longPressTriggered) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Prevent click if long press was triggered (mouse)
    if (mouseLongPressTriggeredRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Call the onTitleClick callback if provided
    if (onTitleClick) {
      onTitleClick();
    }
  };

  return (
    <div className="w-full h-full flex items-center">
      {/* Control button on the left */}
      <div className="flex items-center justify-center pr-2 min-w-[40px] h-full">
        {!valid ? (
          <ErrorIcon style={{ color: "red" }} />
        ) : automationType === "RECURRING" ? (
          <MSwitch
            checked={enabled}
            onChange={handleEnableDisable}
            inputProps={{ "aria-label": "controlled" }}
            size="small"
          />
        ) : running ? (
          <CircularProgress size={20} />
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              handleStartTaskFlow();
              e.stopPropagation();
            }}
            className="disabled:opacity-50"
            style={{ color: "hsl(var(--primary))" }}
          >
            <PlayCircleFilledWhiteIcon style={{ fontSize: "1.25rem" }} />
          </IconButton>
        )}
      </div>

      {/* Title and subtitle section */}
      <div
        className="card-title-trigger flex-1 h-full flex flex-col justify-center cursor-pointer select-none gap-0.5 min-w-0"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* Title */}
        <Typography
          className="overflow-hidden text-ellipsis whitespace-nowrap"
          style={{
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "15px",
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        <Typography
          className="overflow-hidden text-ellipsis whitespace-nowrap opacity-70"
          style={{
            fontSize: "10px",
            fontWeight: 400,
            lineHeight: "13px",
            color: "inherit",
          }}
        >
          {subtitle}
        </Typography>
      </div>
    </div>
  );
};

export default AutomationTitle1Widget;
